"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
exports.__esModule = true;
exports.VideoPlayer = void 0;
var react_1 = __importStar(require("react"));
var classnames_1 = __importDefault(require("classnames"));
var button_1 = require("@kaizen/button");
var hosted_assets_1 = require("@kaizen/hosted-assets");
var Base_module_scss_1 = __importDefault(require("../Base.module.scss"));
var utils_1 = require("../utils");
var usePausePlay_1 = require("./usePausePlay");
var VideoPlayer = function (_a) {
    var _b;
    var _c = _a.autoplay, autoplay = _c === void 0 ? true : _c, _d = _a.loop, loop = _d === void 0 ? false : _d, fallback = _a.fallback, source = _a.source, aspectRatio = _a.aspectRatio, onEnded = _a.onEnded;
    var videoRef = (0, react_1.useRef)(null);
    var _e = react_1["default"].useState(true), prefersReducedMotion = _e[0], setPrefersReducedMotion = _e[1];
    (0, react_1.useEffect)(function () {
        /**
         * Setting `muted` on the player is required in Safari/Webkit and older
         * versions of Chome for the video to autoplay (regardless of whether
         * the format contains an audio stream or not).
         *
         * React does not guarentee the `muted` attribute is set on the
         * `video` element. So on load we force set this attribute. See issue:
         * https://github.com/facebook/react/issues/10389
         */
        var videoElement = videoRef.current;
        if (videoElement !== null) {
            videoElement.setAttribute("muted", "");
        }
    }, []);
    (0, react_1.useEffect)(function () {
        // when the source of the animation is updated, we need to reload the asset
        // to ensure the video player is in sync with the new source.
        var videoElement = videoRef.current;
        if (videoElement !== null) {
            videoElement.load();
        }
    }, [source]);
    (0, react_1.useEffect)(function () {
        if (!window)
            return;
        var reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        setPrefersReducedMotion(reducedMotionQuery.matches);
        var updateMotionPreferences = function () {
            var _a = window.matchMedia("(prefers-reduced-motion: reduce)").matches, matches = _a === void 0 ? false : _a;
            setPrefersReducedMotion(matches);
        };
        var isLegacyEdge = navigator.userAgent.match(/Edge/);
        var isUnsupportedSafari = window.matchMedia("").addEventListener === undefined;
        if (isLegacyEdge || isUnsupportedSafari)
            return;
        reducedMotionQuery.addEventListener("change", updateMotionPreferences, true);
        return function cleanup() {
            reducedMotionQuery.removeEventListener("change", updateMotionPreferences);
        };
    }, []);
    (0, react_1.useEffect)(function () {
        var _a;
        var videoElement = videoRef.current;
        if (!videoElement)
            return;
        if (prefersReducedMotion) {
            videoElement.pause();
        }
        else if (autoplay && !prefersReducedMotion) {
            try {
                // Older browsers may not return a promise, so .play could return undefined
                (_a = videoElement.play()) === null || _a === void 0 ? void 0 : _a["catch"](function () {
                    /*
                     * An DOMException _may_ be raised by some browsers if we
                     * programatically interact with the video before the
                     * user has interacted with the page. This is okay - so
                     * we're going to catch this error without handling it. See:
                     * https://developer.mozilla.org/en-US/docs/Web/Media/Autoplay_guide#autoplay_availability
                     */
                });
            }
            catch (e) {
                /**
                 * Older browsers will raise a synchronous error because their first implementation
                 * of `.play` was not a promise.
                 */
            }
        }
        /**
         * Chrome seems to have an issue with changes to autoplay after the video
         * has been mounted. If there is a change in autoplay we need to force the
         * play() method to be called.
         */
    }, [prefersReducedMotion, autoplay]);
    (0, react_1.useEffect)(function () {
        // Add event listeners for the video element
        var videoElement = videoRef.current;
        if (!videoElement || !onEnded)
            return;
        videoElement.addEventListener("ended", onEnded);
        return function cleanup() {
            videoElement.removeEventListener("ended", onEnded);
        };
    }, [videoRef]);
    var pausePlay = (0, usePausePlay_1.usePausePlay)(videoRef);
    return (react_1["default"].createElement("figure", { className: (0, classnames_1["default"])(Base_module_scss_1["default"].figure, (_b = {},
            _b["".concat(Base_module_scss_1["default"][aspectRatio], " ").concat(Base_module_scss_1["default"].aspectRatioWrapper)] = Boolean(aspectRatio),
            _b)) },
        react_1["default"].createElement("video", { muted: true, "aria-hidden": true, preload: "metadata", ref: videoRef, width: "100%", "data-testid": "kz-video-player", className: Base_module_scss_1["default"].wrapper, loop: loop, poster: (0, hosted_assets_1.assetUrl)("".concat(fallback, ".png")), autoPlay: prefersReducedMotion ? false : autoplay, playsInline: true, tabIndex: -1 },
            (0, utils_1.canPlayWebm)() && (react_1["default"].createElement("source", { src: (0, hosted_assets_1.assetUrl)("".concat(source, ".webm")), type: "video/webm" })),
            react_1["default"].createElement("source", { src: (0, hosted_assets_1.assetUrl)("".concat(source, ".mp4")), type: "video/mp4" })),
        react_1["default"].createElement(button_1.IconButton, { onClick: function () { return pausePlay.toggle(); }, icon: pausePlay.icon, label: pausePlay.label, classNameOverride: Base_module_scss_1["default"].pausePlayButton })));
};
exports.VideoPlayer = VideoPlayer;
//# sourceMappingURL=VideoPlayer.js.map